import { useMemo, useState } from 'react'
import { Alert, App, Button, Flex, Form, Input, Modal, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LockOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { SCOPE_TYPES, type ScopeType } from '@/shared/auth/permissions'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PermissionPicker } from '@/features/identity/PermissionPicker'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  createRole,
  deleteRole,
  fetchPermissionCatalog,
  fetchRolePage,
  setRolePermissions,
  type Role,
} from '@/features/identity/identityApi'
import { roleName } from '@/shared/lib/roleName'
import { permissionLabel } from '@/features/identity/permissionLabel'

interface CreateForm {
  code: string
  name: string
  defaultScope: ScopeType
}

/** Backend `CreateRoleCommand` dagi naqsh bilan AYNAN bir xil */
const CODE_SHAPE = /^[A-Z][A-Z0-9_]{1,31}$/

/**
 * Rollar — yaratish, nusxa olish va huquqlarini tahrirlash.
 *
 * <h2>⚠️ TIZIM ROLI TAHRIRLANMAYDI — NUSXA OLINADI</h2>
 * Server tizim rolini o'zgartirishni rad etadi: ular migratsiya bilan
 * kelib turadi va qo'lda kiritilgan o'zgarish keyingi yangilanishda
 * jimgina yo'qolardi. Shuning uchun panel ularga tahrirlash tugmasini
 * KO'RSATMAYDI, o'rniga "nusxa olish" beradi — bu serverning
 * "nusxa olib tahrirlang" degan javobining amaldagi ko'rinishi.
 *
 * <h2>⚠️ HUQUQLARNI SAQLASH — ALMASHTIRISH, QO'SHISH EMAS</h2>
 * `PUT /permissions` butun to'plamni almashtiradi. Rolda panel
 * katalogida yo'q kod bo'lsa (backend yangi huquq qo'shgan, panel hali
 * yangilanmagan), u yuborilmasa JIMGINA o'chib ketardi. Shuning uchun
 * bunday kodlar saqlanadi va operatorga aniq aytiladi.
 */
export function RolesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  /*
    ⚠️ BUG-048 — tasdiqlash oynasi UCHUN `App.useApp().modal`, statik
    `Modal.confirm` EMAS. Statik chaqiruv `ConfigProvider` kontekstidan
    tashqarida ildiz yasaydi va qorong'i panel ustiga OQ dialog chizadi
    (o'lchov `SettingsPage` dagi izohda). Sahifadagi `<Modal>` JSX
    elementlari esa daraxt ichida — ular O'ZGARISHSIZ qoladi.
  */
  const { modal } = App.useApp()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  /** O'chirish xatosi — sabab `remove` mutatsiyasi javadoc'ida */
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [form] = Form.useForm<CreateForm>()

  const catalog = useQuery({
    queryKey: queryKeys.identity.permissionCatalog,
    queryFn: fetchPermissionCatalog,
    enabled: can('role.read'),
  })

  const dangerous = useMemo(
    () => new Set<string>((catalog.data ?? []).filter((p) => p.dangerous).map((p) => p.code)),
    [catalog.data],
  )

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.identity.roles })
  }

  const create = useMutation({
    mutationFn: (values: CreateForm) =>
      createRole({ ...values, permissions: picked }),
    onSuccess: () => {
      setCreating(false)
      form.resetFields()
      setPicked([])
      refresh()
    },
    // ⚠️ `onError` ATAYLAB yo'q: har ikkala mutatsiya ham O'Z OYNASIGA
    // bog'langan va xatosi sahifa banneriga chiqsa, modal niqobi ortida
    // qolardi. ⚠️ Ikki OYNA, ikki MUTATSIYA — ular alohida tozalanadi,
    // aks holda "Yaratish" xatosi "Tahrirlash" oynasida chiqib qolardi.
  })

  const save = useMutation({
    mutationFn: (role: Role) =>
      // ⚠️ Panel bilmaydigan kodlar ham qaytariladi — yuqoridagi izoh.
      setRolePermissions(role.id, [...picked, ...role.unknownPermissions]),
    onSuccess: () => {
      setEditing(null)
      setPicked([])
      refresh()
    },
    // ⚠️ `onError` ATAYLAB yo'q — yuqoridagi izoh.
  })

  /**
   * Rolni o'chirish — D2 (tashqi tekshiruvchi: "Roles have no delete
   * path").
   *
   * <h2>⚠️ TUGMA FAQAT O'CHIRISH MUMKIN BO'LGAN QATORDA</h2>
   * Server ikki holatda rad etadi: TIZIM roli va EGASI BOR rol
   * (`RoleService#deleteRole`). Ikkalasi ham qatorda ALLAQACHON
   * ko'rinib turadi — "Tizim" yorlig'i va "Foydalanuvchilar" ustuni.
   * Shu sababli tugma o'sha qatorlarda umuman CHIZILMAYDI: bosilib,
   * 409 bilan tugaydigan tugma operatorga nima qilishni aytmaydi.
   *
   * <h2>⚠️ Xato SAHIFA banneriga chiqadi, oyna ORTIGA emas</h2>
   * Bu mutatsiya OYNAGA bog'lanmagan (`Modal.confirm` — antd ning o'z
   * oynasi va u javobni kutmaydi), shuning uchun natija bannerga
   * chiqishi TO'G'RI: aks holda 409 ("rolda 3 ta xodim bor") hech
   * qayerda ko'rinmasdi.
   */
  const remove = useMutation({
    mutationFn: (role: Role) => deleteRole(role.id),
    onSuccess: () => {
      setDeleteError(null)
      refresh()
    },
    onError: (err) => setDeleteError(translateError(err, t)),
  })

  const confirmDelete = (role: Role) => {
    setDeleteError(null)
    modal.confirm({
      title: t('roles.deleteTitle', { name: roleName(role, t) }),
      content: t('roles.deleteHint'),
      okText: t('roles.delete'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () => remove.mutateAsync(role).catch(() => {
        // ⚠️ Xato YUTILMAYDI — u `onError` da bannerga yozildi.
        // Bu `catch` faqat antd oynasini yopish uchun: aks holda
        // rad etilgan va'da konsolga "unhandled rejection" berardi.
      }),
    })
  }

  /**
   * Huquqlarni saqlash — TASDIQ BILAN.
   *
   * ⚠️ Nega kerak: bu amal bir bosishda o'nlab odamdan huquq oladi va
   * DARHOL kuchga kiradi — ular uchun bu "panel buzildi" bo'lib
   * ko'rinadi, ortga qaytarish esa qo'lda. Sahifada nechta odamga
   * tegishi banner bilan aytilardi, lekin banner bosishni TO'XTATMASDI.
   *
   * ⚠️ Matn UMUMIY EMAS: aynan qaysi huquqlar OLINAYOTGANI va
   * QO'SHILAYOTGANI sanab o'tiladi. "Davom etasizmi?" bu yerda hech
   * narsa aytmaydi — operator PermissionPicker'dagi 112 katakchadan
   * qaysi biri o'zgarganini eslab qololmaydi.
   *
   * O'zgarish bo'lmasa tasdiq so'ralmaydi: bu qaytariladigan (aslida
   * hech narsa qilmaydigan) amal.
   */
  const confirmSave = (role: Role) => {
    // ⚠️ `Set<string>` ATAYLAB, `Set<Permission>` emas: `picked` da
    // panel katalogidan tashqaridagi kod ham bo'lishi mumkin va farqni
    // KOD BO'YICHA solishtirish kerak, tiplar kesishuvi bo'yicha emas.
    const before = new Set<string>(role.permissions)
    const after = new Set<string>(picked)
    const removed = [...before].filter((code) => !after.has(code))
    const added = [...after].filter((code) => !before.has(code))

    if (removed.length === 0 && added.length === 0) {
      save.mutate(role)
      return
    }

    const addedDangerous = added.filter((code) => dangerous.has(code))

    modal.confirm({
      title: t('roles.saveConfirmTitle', { name: roleName(role, t) }),
      width: 560,
      content: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {role.userCount > 0 ? (
            <Typography.Text strong>
              {t('roles.saveConfirmAffects', { count: role.userCount })}
            </Typography.Text>
          ) : (
            <Typography.Text>{t('roles.saveConfirmNoUsers')}</Typography.Text>
          )}
          {removed.length > 0 ? (
            <Typography.Text type="danger">
              {t('roles.saveConfirmRemoved', {
                count: removed.length,
                codes: removed.map((code) => permissionLabel(code, t)).join(', '),
              })}
            </Typography.Text>
          ) : null}
          {added.length > 0 ? (
            <Typography.Text>
              {t('roles.saveConfirmAdded', {
                count: added.length,
                codes: added.map((code) => permissionLabel(code, t)).join(', '),
              })}
            </Typography.Text>
          ) : null}
          {addedDangerous.length > 0 ? (
            <Typography.Text type="warning">
              {t('roles.saveConfirmDangerous', {
                codes: addedDangerous.map((code) => permissionLabel(code, t)).join(', '),
              })}
            </Typography.Text>
          ) : null}
          <Typography.Text type="secondary">{t('roles.saveConfirmHint')}</Typography.Text>
        </Space>
      ),
      okText: t('common.save'),
      okButtonProps: { danger: removed.length > 0 || addedDangerous.length > 0 },
      cancelText: t('common.cancel'),
      onOk: () =>
        save.mutateAsync(role).catch(() => {
          // Xato oyna ichidagi `Alert` da ko'rsatiladi (`save.isError`);
          // bu `catch` faqat "unhandled rejection" ni to'xtatadi.
        }),
    })
  }

  /** Bekor qilib chiqish — har oyna FAQAT o'z mutatsiyasini tozalaydi */
  const closeCreate = () => {
    create.reset()
    setCreating(false)
  }

  const closeEditor = () => {
    save.reset()
    setEditing(null)
  }

  /** Tizim rolidan nusxa: huquqlari ko'chiriladi, kod va nom yangi */
  const startCopy = (role: Role) => {
    setPicked([...role.permissions])
    form.setFieldsValue({
      code: '',
      // ⚠️ NUSXA nomiga TARJIMA tushadi va bu ataylab: nusxa endi
      // tizim roli EMAS, ya'ni uning nomi shu lahzada muzlaydi.
      // Xom o'zbekcha nom ruscha panelda yangi rol nomiga aylanib
      // qolardi (N5).
      name: t('roles.copyOf', { name: roleName(role, t) }),
      defaultScope: role.defaultScope,
    })
    setCreating(true)
  }

  const columns: ColumnsType<Role> = [
    {
      /*
        ⚠️ Ustun kaliti `name`, chunki SERVER shu enum bo'yicha saralaydi
        (`NAME`). Kalit ekranda ko'rinmaydi, lekin u `sortField` ga
        aylanadi — mos kelmagan nom serverda 400 berardi.
      */
      key: 'name',
      title: t('roles.role'),
      width: 260,
      sorter: true,
      render: (_, role) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <Typography.Text strong>{roleName(role, t)}</Typography.Text>
            {role.system ? (
              <Tooltip title={t('roles.systemHint')}>
                <Tag icon={<LockOutlined />}>{t('roles.system')}</Tag>
              </Tooltip>
            ) : null}
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {role.code}
          </Typography.Text>
        </Space>
      ),
    },
    {
      /*
        ⚠️ ILGARI BU YERDA «Rol turi» FILTRI (tanlagich) TURARDI.

        Server bunday filtrni bilmaydi, ro'yxat esa endi SAHIFALANGAN:
        mijozdagi filtr faqat JORIY sahifani toraytirardi va operator
        «tizim rollari yo'q ekan» degan yolg'on xulosaga kelardi.
        O'rniga SARALASH qoldi — u serverda (`IS_SYSTEM`) bajariladi va
        butun ro'yxatga ta'sir qiladi.

        ⚠️ Ilgari server sukut tartibi `is_system, code` edi, ya'ni tizim
        rollari DOIM tepada turardi. Endi sukut `CREATED_AT DESC` —
        o'sha guruhlash uchun shu ustun bosiladi.
      */
      key: 'system',
      title: t('roles.kindFilter'),
      width: 140,
      sorter: true,
      render: (_, role) => (role.system ? t('roles.system') : t('roles.custom')),
    },
    {
      key: 'scope',
      title: t('roles.defaultScope'),
      width: 130,
      render: (_, role) => t(`identity.scope.${role.defaultScope}`, role.defaultScope),
    },
    {
      key: 'permissions',
      title: t('roles.permissionCount'),
      width: 150,
      align: 'right',
      render: (_, role) => {
        const risky = role.permissions.filter((p) => dangerous.has(p)).length
        return (
          <Space size={4}>
            <Typography.Text>{role.permissions.length + role.unknownPermissions.length}</Typography.Text>
            {risky > 0 ? (
              <Tooltip title={t('roles.dangerousCount', { count: risky })}>
                <Tag color="red">{risky}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        )
      },
    },
    {
      key: 'users',
      title: t('roles.userCount'),
      width: 130,
      align: 'right',
      /*
        ⚠️ Foydalanuvchilar soni ATAYLAB ko'rinadi: huquqni olib tashlash
        shu odamlarning hammasiga DARHOL ta'sir qiladi. Sonsiz jadval
        "bu rolni hech kim ishlatmayapti" degan noto'g'ri taassurot
        berardi.
      */
      render: (_, role) => role.userCount,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, role) => (
        // Huquq va TIZIM roli sharti `RowActions` ichida filtrlanadi —
        // mantiq o'zgarmadi, faqat ko'rinish. Tizim rolida faqat
        // "Nusxa olish", qolganida tahrirlash va o'chirish.
        <RowActions
          items={[
            {
              key: 'copy',
              label: <Tooltip title={t('roles.copyHint')}>{t('roles.copy')}</Tooltip>,
              onClick: () => startCopy(role),
              permission: 'role.create',
              hidden: !role.system,
            },
            {
              key: 'editPermissions',
              label: t('roles.editPermissions'),
              onClick: () => {
                setEditing(role)
                setPicked([...role.permissions])
              },
              permission: 'role.update',
              hidden: role.system,
            },
            {
              /*
                ⚠️ D2 — o'chirish yo'li. Amal FAQAT bo'sh rolda bosiladi:
                egasi bor rolni server 409 bilan rad etadi va bu qatorda
                "Foydalanuvchilar" ustuni bo'yicha allaqachon ko'rinib
                turadi (`remove` javadoc'i). Sabab yorliq tooltipida.
              */
              key: 'delete',
              label:
                role.userCount === 0 ? (
                  t('roles.delete')
                ) : (
                  <Tooltip title={t('roles.deleteBlocked', { count: role.userCount })}>
                    {t('roles.delete')}
                  </Tooltip>
                ),
              onClick: () => confirmDelete(role),
              permission: 'role.delete',
              disabled: role.userCount !== 0,
              loading: remove.isPending,
              danger: true,
              divider: true,
              hidden: role.system,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('roles.title')}
        description={t('roles.subtitle')}
        extra={
          <Can permission="role.create">
            <Button
              type="primary"
              onClick={() => {
                setPicked([])
                form.resetFields()
                setCreating(true)
              }}
            >
              {t('roles.create')}
            </Button>
          </Can>
        }
      />

      {/*
        ⚠️ O'CHIRISH XATOSI SAHIFADA — `remove` javadoc'i: u antd ning
        `Modal.confirm` iga bog'langan va o'sha oyna javobni kutmasdan
        yopiladi, ya'ni xabar boshqa joyda ko'rinmasdi.
      */}
      {deleteError ? (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          message={deleteError}
          onClose={() => setDeleteError(null)}
        />
      ) : null}

      {/*
        ⚠️ QOIDA JADVAL TEPASIDA. Operator o'zida bo'lmagan huquqni
        bera olmasligini rolni tuzishdan OLDIN bilishi kerak: aks holda
        u belgilay olmagan katakchani "nosozlik" deb o'ylardi.
      */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('roles.ruleTitle')}
        description={t('roles.ruleHint')}
      />

      {/*
        ⚠️ SAHIFALASH, QIDIRUV VA SARALASH — SERVERDA (2026-08-20).
        `GET /api/roles` endi `page`/`size`/`search`/`sortField`/
        `sortOrder` ni qabul qiladi va `{rows,total,page,size}` qaytaradi.
        Ilgari bu yerda butun ro'yxat olinib mijozda filtrlanardi — endi
        u faqat joriy sahifani ko'rardi, ya'ni «topilmadi» yolg'on
        bo'lardi.

        ⚠️ Ro'yxat XATOSI uchun alohida banner endi YO'Q — uni
        `DataTable` o'zi chizadi va «qayta urinish» tugmasini ham beradi.
      */}
      <DataTable<Role>
        queryKey={queryKeys.identity.roles}
        fetchPage={fetchRolePage}
        columns={columns}
        rowKey={(role) => role.id}
        tableId="identity-roles"
        stickyFirstColumn
        searchable
        searchPlaceholder={t('roles.searchPlaceholder')}
        // 56+260+140+130+150+130+56 = 922
        scrollX={922}
        emptyTitle={t('roles.emptyTitle')}
        emptyDescription={t('roles.emptyHint')}
      />

      <Modal
        open={creating}
        title={t('roles.create')}
        width={860}
        onCancel={closeCreate}
        onOk={() => submitOnValid(form, (values) => create.mutate(values))}
        confirmLoading={create.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/* 409 (takroriy kod) AYNAN shu yerda qoladi */}
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          {/*
            ⚠️ `Space` EMAS, `Flex ... wrap` — 390px da IKKI BOSHQARUV
            EKRANDAN CHIQIB KETARDI (2026-08-14 tashqi tester, B9).

            O'lchangan: oyna 390px, ichki `scrollWidth` esa 688px.
            "Name" maydoni `left: 256 → right: 476`, "Default scope"
            tanlagichi `left: 500 → right: 676` — ya'ni ikkalasi ham
            ko'rinadigan sohadan tashqarida edi. 32 ta mobil dialog
            ichida YAGONA shunday joy.

            Sabab: `Space` gorizontal qatorni O'RAMAYDI, uchala
            maydonning eng kichik eni esa 220+220+200+24 = 664px.

            Yechim: `wrap` + `flex: '1 1 220px'`. Keng oynada (860px)
            uchtasi bir qatorda qoladi — 664 < 812 — ya'ni desktop
            ko'rinishi O'ZGARMAYDI; tor ekranda esa ular pastma-past
            tushadi. `minWidth: 0` — flex elementining `min-content`
            poli uzun yorliq tufayli qatorni yana toshirmasin.
          */}
          <Flex gap={12} align="start" wrap>
            <Form.Item
              name="code"
              label={t('roles.code')}
              style={{ flex: '1 1 220px', minWidth: 0 }}
              // Naqsh serverdagi CHECK bilan bir xil — xatoni shu yerda
              // aytish "Internal Server Error" dan ancha foydali.
              rules={[
                { required: true, message: t('roles.codeRequired') },
                { pattern: CODE_SHAPE, message: t('roles.codeShape') },
              ]}
            >
              <Input placeholder="SUPPORT_L1" />
            </Form.Item>
            <Form.Item
              name="name"
              label={t('roles.name')}
              style={{ flex: '1 1 220px', minWidth: 0 }}
              rules={[{ required: true, message: t('roles.nameRequired') }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="defaultScope"
              label={t('roles.defaultScope')}
              style={{ flex: '1 1 200px', minWidth: 0 }}
              initialValue="TENANT"
              rules={[{ required: true }]}
            >
              <Select
                options={SCOPE_TYPES.map((s) => ({ value: s, label: t(`identity.scope.${s}`, s) }))}
              />
            </Form.Item>
          </Flex>

          <Typography.Text type="secondary">{t('roles.scopeHint')}</Typography.Text>

          <div style={{ marginTop: 16 }}>
            <PermissionPicker value={picked} onChange={setPicked} dangerous={dangerous} />
          </div>
        </Form>
      </Modal>

      <Modal
        open={editing !== null}
        title={t('roles.editTitleFor', { name: editing?.name ?? '' })}
        width={860}
        onCancel={closeEditor}
        onOk={() => editing && confirmSave(editing)}
        confirmLoading={save.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {save.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={translateError(save.error, t)}
          />
        ) : null}

        {/*
          ⚠️ Nechta odamga ta'sir qilishi SAQLASHDAN OLDIN aytiladi.
          Huquqni olib tashlash o'sha odamlarda darhol kuchga kiradi va
          ular buni "panel buzildi" deb tushunadi.
        */}
        {editing && editing.userCount > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('roles.affects', { count: editing.userCount })}
            description={t('roles.affectsHint')}
          />
        ) : null}

        {/*
          ⚠️ Panel bilmaydigan kodlar — ular saqlanadi, lekin ko'rsatiladi:
          "nega huquqlar soni ro'yxatdagidan ko'p" degan savol tug'ilmasin.
        */}
        {editing && editing.unknownPermissions.length > 0 ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('roles.unknownKept', { count: editing.unknownPermissions.length })}
            description={editing.unknownPermissions.join(', ')}
          />
        ) : null}

        <PermissionPicker value={picked} onChange={setPicked} dangerous={dangerous} />
      </Modal>
    </>
  )
}
